import React, { useState, useEffect, useRef } from "react";
import { Input, Button, Avatar, Spin } from "antd";
import { SendOutlined, UserOutlined } from "@ant-design/icons";
import { chatService } from "../../services/chatService";

const ChatWindow = ({ chat, userId, userName, onBack }) => {
  const [messages, setMessages] = useState([]);
  const [content, setContent] = useState("");
  const [loading, setLoading] = useState(true);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (!chat.id) return;
    const unsubscribe = chatService.subscribeToMessages(chat.id, (data) => {
      setMessages(data);
      setLoading(false);
    });
    return () => unsubscribe();
  }, [chat.id]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const handleSend = async () => {
    if (!content.trim()) return;
    const msg = content;
    setContent("");
    
    // participants logic for notifications
    const participants = chat.participants.map(id => {
        const pData = chat.participantsData[id];
        return {
            id,
            type: pData?.role === 'agent' || pData?.type === 'agent' ? 'agent' : 'user'
        };
    });

    try {
        await chatService.sendMessage(chat.id, userId, msg, participants);
    } catch (error) {
        console.error("Failed to send message:", error);
    }
  };

  return (
    <div className="flex flex-col h-full bg-white overflow-hidden relative">
      <div 
        ref={scrollRef}
        className="flex-1 overflow-y-auto p-4 flex flex-col gap-4 bg-gray-50 scroll-smooth"
      >
        {loading ? (
          <div className="flex justify-center items-center h-full"><Spin size="large" /></div>
        ) : (
          messages.map((m, i) => {
            const isSelf = m.senderId === userId;
            const senderInfo = chat.participantsData[m.senderId];
            const showName = chat.isGroup && !isSelf;

            return (
              <div 
                key={m.id || i} 
                className={`flex flex-col ${isSelf ? 'items-end' : 'items-start'}`}
              >
                {showName && (
                  <span className="text-[10px] text-gray-500 mb-1 ml-1 font-medium">{senderInfo?.name}</span>
                )}
                <div className={`flex gap-2 max-w-[85%] ${isSelf ? 'flex-row-reverse' : 'flex-row'}`}>
                  {!isSelf && (
                     <Avatar 
                        size="small" 
                        className="mt-auto bg-gray-300"
                        src={senderInfo?.avatar}
                     >
                        {senderInfo?.name?.[0]?.toUpperCase()}
                     </Avatar>
                  )}
                  <div 
                    className={`px-4 py-2.5 rounded-2xl shadow-sm ${
                      isSelf ? 'bg-[#3073f1] text-white rounded-tr-none' : 'bg-white text-gray-800 rounded-tl-none border border-gray-100'
                    }`}
                  >
                    <p className="text-[13px] m-0 leading-relaxed whitespace-pre-wrap">{m.content}</p>
                    <div className={`text-[9px] mt-1 flex items-center gap-1 ${isSelf ? 'text-blue-100 italic' : 'text-gray-400'}`}>
                       {new Date(m.timestamp?.seconds * 1000 || Date.now()).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                       {isSelf && m.readBy?.length > 1 && <span> • Seen</span>}
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      <div className="p-4 border-t bg-white sticky bottom-0 z-10 w-full">
        <div className="flex gap-2 items-center bg-gray-50 rounded-2xl px-3 py-1.5 border border-gray-200 focus-within:border-[#3073f1] focus-within:bg-white transition-all shadow-inner">
          <Input.TextArea
            autoSize={{ minRows: 1, maxRows: 4 }}
            placeholder="Write a message..."
            value={content}
            onChange={(e) => setContent(e.target.value)}
            onPressEnter={(e) => {
              if (!e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
            className="bg-transparent border-none focus:shadow-none p-2 resize-none overflow-hidden text-sm"
            style={{ boxShadow: 'none' }}
          />
          <Button 
            type="primary" 
            shape="circle" 
            icon={<SendOutlined />} 
            onClick={handleSend}
            disabled={!content.trim()}
            className="flex items-center justify-center bg-[#3073f1] h-9 w-9 min-w-[36px]"
          />
        </div>
      </div>
    </div>
  );
};

export default ChatWindow;
